/**
 * MRR - Monthly Revenue Report
 *
 * Cash-in and lead-to-cash numbers, date-range aware, source-filterable.
 * KPI row → Cash-In gauge → Cash Collection Cohort → Lead-to-Cash table → Lead Payment Tracker
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { branding } from "../config/branding";
import { PageHeader } from "../components";
import { CashCollectionCohortChart } from "./BuyingCycle";
import { useDateRange } from "../context/DateRangeContext";
import { useFilters } from "../context/FilterContext";

// ─── MRR Types ─────────────────────────────────────────────────────────────────

interface MrrKpis {
  expectedCash: number;
  collectedCash: number;
  pendingCash: number;
  collectionPct: number;
  closedWonDeals: number;
}

interface FunnelRow {
  stage: string;
  count: number;
  expected: number | null;
  collected: number | null;
  pending: number | null;
  convPct: number | null;
}

interface MrrData {
  kpis: MrrKpis;
  funnel: FunnelRow[];
  sources: string[];
}

interface LeadsApiResponse {
  leads: PaymentLead[];
  programs: string[];
  reps: string[];
}

// ─── Payment Tracker Types ──────────────────────────────────────────────────────

type PaymentStatus = "Not Paid" | "Deposit Paid" | "Partially Paid" | "Fully Paid" | "Overdue" | "Refunded";

interface PaymentLead {
  id: string;
  name: string;
  phone: string;
  email: string;
  dealId: string;
  program: string;
  dealValue: number;
  amountPaid: number;
  amountPending: number;
  status: PaymentStatus;
  paymentDate: string | null;
  dueDate: string | null;
  salesRep: string;
  notes: string;
  closeDate: string;
  lastContacted: string | null;
}

const ALL_STATUSES: PaymentStatus[] = ["Not Paid", "Deposit Paid", "Partially Paid", "Fully Paid", "Overdue", "Refunded"];

// ─── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(Math.abs(n));

const fmtDate = (s: string | null) => {
  if (!s) return "—";
  const d = new Date(s + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" });
};

const daysSince = (dateStr: string) => {
  const d = new Date(dateStr + "T00:00:00");
  const now = new Date();
  return Math.max(0, Math.floor((now.getTime() - d.getTime()) / 86_400_000));
};

const agingBucket = (days: number) => {
  if (days <= 3)  return "0–3d";
  if (days <= 7)  return "4–7d";
  if (days <= 14) return "8–14d";
  return "15+d";
};

// Title-case normalizer — handles all-caps and all-lowercase entries from EOC
const toTitleCase = (s: string) =>
  s.replace(/\w+/g, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());

// rgba helper — uses hex from branding, adds alpha
function rgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

const STATUS_STYLE: Record<PaymentStatus, { color: string; bg: string }> = {
  "Fully Paid":     { color: branding.colors.chart[0], bg: rgba(branding.colors.chart[0], 0.12) },
  "Deposit Paid":   { color: branding.colors.chart[1], bg: rgba(branding.colors.chart[1], 0.12) },
  "Partially Paid": { color: branding.colors.chart[2], bg: rgba(branding.colors.chart[2], 0.12) },
  "Not Paid":       { color: "var(--muted-foreground)", bg: "var(--muted)" },
  "Overdue":        { color: branding.colors.primary,   bg: rgba(branding.colors.primary, 0.10) },
  "Refunded":       { color: branding.colors.chart[4],  bg: rgba(branding.colors.chart[4], 0.20) },
};

const STATUS_BAR_COLOR: Record<PaymentStatus, string> = {
  "Fully Paid":     branding.colors.chart[0],
  "Deposit Paid":   branding.colors.chart[1],
  "Partially Paid": branding.colors.chart[2],
  "Not Paid":       rgba(branding.colors.chart[4], 0.35),
  "Overdue":        branding.colors.primary,
  "Refunded":       branding.colors.chart[4],
};

// ─── StatusBadge ───────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: PaymentStatus }) {
  const s = STATUS_STYLE[status];
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap"
      style={{ color: s.color, background: s.bg }}>
      {status}
    </span>
  );
}

// ─── Tracker KPI Row ───────────────────────────────────────────────────────────

function TrackerKpiRow({ leads }: { leads: PaymentLead[] }) {
  const total        = leads.length;
  const fullyPaid    = leads.filter(l => l.status === "Fully Paid").length;
  const unpaid       = leads.filter(l => l.status === "Not Paid" || l.status === "Overdue").length;
  const partial      = leads.filter(l => l.status === "Partially Paid" || l.status === "Deposit Paid").length;
  const pendingCash  = leads.filter(l => l.amountPending > 0).reduce((s, l) => s + l.amountPending, 0);
  const overdueCash  = leads.filter(l => l.status === "Overdue").reduce((s, l) => s + l.amountPending, 0);

  const items = [
    { label: "Leads Tracked",  value: String(total),       accent: false },
    { label: "Fully Paid",     value: String(fullyPaid),   accent: true  },
    { label: "Unpaid",         value: String(unpaid),      accent: false },
    { label: "Partially Paid", value: String(partial),     accent: false },
    { label: "Pending Cash",   value: fmt(pendingCash),    accent: false },
    { label: "Overdue Cash",   value: fmt(overdueCash),    accent: false, warn: true },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
      {items.map(item => (
        <div key={item.label} className="rounded-xl border bg-card p-3.5 flex flex-col gap-1"
          style={item.warn && overdueCash > 0 ? { borderColor: rgba(branding.colors.primary, 0.35) } : undefined}>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{item.label}</span>
          <span className="text-xl font-bold tabular-nums"
            style={item.warn && overdueCash > 0 ? { color: branding.colors.primary }
                 : item.accent ? { color: branding.colors.chart[0] } : undefined}>
            {item.value}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── Searchable Lead Payment Table ─────────────────────────────────────────────

const PAGE_SIZE = 8;

function LeadPaymentTable({ leads, programs, reps }: { leads: PaymentLead[]; programs: string[]; reps: string[] }) {
  const [search,    setSearch]    = useState("");
  const [statusF,   setStatusF]   = useState<PaymentStatus | "">("");
  const [programF,  setProgramF]  = useState("");
  const [repF,      setRepF]      = useState("");
  const [pendingF,  setPendingF]  = useState<"any" | "pending" | "none">("any");
  const [page,      setPage]      = useState(1);
  const [actionRow, setActionRow] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return leads.filter(l => {
      if (q && ![toTitleCase(l.name), l.phone, l.email, l.dealId, l.status].join(" ").toLowerCase().includes(q)) return false;
      if (statusF  && l.status  !== statusF)  return false;
      if (programF && l.program !== programF) return false;
      if (repF     && l.salesRep !== repF)    return false;
      if (pendingF === "pending" && l.amountPending <= 0) return false;
      if (pendingF === "none"    && l.amountPending >  0) return false;
      return true;
    });
  }, [leads, search, statusF, programF, repF, pendingF]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows   = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetPage = () => setPage(1);

  const selectCls = "h-8 text-xs rounded-md border border-border bg-background px-2 text-foreground focus:outline-none focus:ring-1 focus:ring-[var(--brand-ring)] min-w-[120px]";

  return (
    <div className="rounded-xl border bg-card overflow-hidden">
      {/* Toolbar */}
      <div className="px-5 pt-5 pb-4 border-b border-border space-y-3">
        {/* Search */}
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text" value={search}
            onChange={e => { setSearch(e.target.value); resetPage(); }}
            placeholder="Search by lead name, phone, email, deal ID, or payment status…"
            className="w-full h-9 pl-9 pr-4 text-sm rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-[var(--brand-ring)]"
          />
        </div>
        {/* Filter row */}
        <div className="flex flex-wrap gap-2 items-center">
          <select value={statusF} onChange={e => { setStatusF(e.target.value as PaymentStatus | ""); resetPage(); }} className={selectCls}>
            <option value="">All Statuses</option>
            {ALL_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={programF} onChange={e => { setProgramF(e.target.value); resetPage(); }} className={selectCls}>
            <option value="">All Programs</option>
            {programs.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={repF} onChange={e => { setRepF(e.target.value); resetPage(); }} className={selectCls}>
            <option value="">All Reps</option>
            {reps.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <select value={pendingF} onChange={e => { setPendingF(e.target.value as "any" | "pending" | "none"); resetPage(); }} className={selectCls}>
            <option value="any">Any Pending</option>
            <option value="pending">Has Pending</option>
            <option value="none">Fully Settled</option>
          </select>
          {(search || statusF || programF || repF || pendingF !== "any") && (
            <button onClick={() => { setSearch(""); setStatusF(""); setProgramF(""); setRepF(""); setPendingF("any"); resetPage(); }}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1">
              Clear filters
            </button>
          )}
          <span className="ml-auto text-xs text-muted-foreground">{filtered.length} lead{filtered.length !== 1 ? "s" : ""}</span>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[1100px]">
          <thead className="sticky top-0 z-10 bg-card border-b border-border">
            <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3 text-left font-semibold">Lead Name</th>
              <th className="px-3 py-3 text-left font-semibold">Phone</th>
              <th className="px-3 py-3 text-left font-semibold">Email</th>
              <th className="px-3 py-3 text-left font-semibold">Deal ID</th>
              <th className="px-3 py-3 text-left font-semibold">Program</th>
              <th className="px-3 py-3 text-right font-semibold">Deal Value</th>
              <th className="px-3 py-3 text-right font-semibold">Paid</th>
              <th className="px-3 py-3 text-right font-semibold">Pending</th>
              <th className="px-3 py-3 text-left font-semibold">Status</th>
              <th className="px-3 py-3 text-left font-semibold">Pay Date</th>
              <th className="px-3 py-3 text-left font-semibold">Due Date</th>
              <th className="px-3 py-3 text-left font-semibold">Rep</th>
              <th className="px-3 py-3 text-left font-semibold">Notes</th>
              <th className="px-3 py-3 text-center font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr><td colSpan={14} className="py-12 text-center text-sm text-muted-foreground">No leads match the current filters.</td></tr>
            ) : pageRows.map((row, i) => (
              <tr key={row.id}
                className={`border-b border-border/40 transition-colors hover:bg-muted/30 ${i % 2 !== 0 ? "bg-muted/10" : ""}`}
                style={row.status === "Overdue" ? { borderLeft: `2px solid ${rgba(branding.colors.primary, 0.6)}` } : undefined}>
                <td className="px-4 py-3 font-semibold text-foreground whitespace-nowrap">{toTitleCase(row.name)}</td>
                <td className="px-3 py-3 text-muted-foreground text-xs whitespace-nowrap">{row.phone}</td>
                <td className="px-3 py-3 text-muted-foreground text-xs max-w-[160px] truncate">{row.email}</td>
                <td className="px-3 py-3 text-xs font-mono text-muted-foreground">{row.dealId}</td>
                <td className="px-3 py-3 text-xs whitespace-nowrap">{row.program}</td>
                <td className="px-3 py-3 text-right font-medium tabular-nums">{fmt(row.dealValue)}</td>
                <td className="px-3 py-3 text-right tabular-nums font-semibold"
                  style={{ color: row.amountPaid > 0 ? branding.colors.chart[0] : undefined }}>
                  {row.amountPaid !== 0 ? fmt(row.amountPaid) : "—"}
                </td>
                <td className="px-3 py-3 text-right tabular-nums font-semibold"
                  style={{ color: row.amountPending > 0 ? branding.colors.primary : "var(--muted-foreground)" }}>
                  {row.amountPending > 0 ? fmt(row.amountPending) : "—"}
                </td>
                <td className="px-3 py-3"><StatusBadge status={row.status} /></td>
                <td className="px-3 py-3 text-xs text-muted-foreground whitespace-nowrap">{fmtDate(row.paymentDate)}</td>
                <td className="px-3 py-3 text-xs whitespace-nowrap"
                  style={row.dueDate && new Date(row.dueDate) < new Date() && row.amountPending > 0
                    ? { color: branding.colors.primary, fontWeight: 600 } : { color: "var(--muted-foreground)" }}>
                  {fmtDate(row.dueDate)}
                </td>
                <td className="px-3 py-3 text-xs whitespace-nowrap">{row.salesRep.split(" ")[0]}</td>
                <td className="px-3 py-3 text-xs text-muted-foreground max-w-[140px] truncate">{row.notes || "—"}</td>
                <td className="px-3 py-3 text-center relative">
                  <button onClick={() => setActionRow(actionRow === row.id ? null : row.id)}
                    className="inline-flex items-center justify-center w-6 h-6 rounded hover:bg-muted transition-colors">
                    <svg className="w-4 h-4 text-muted-foreground" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                    </svg>
                  </button>
                  {actionRow === row.id && (
                    <div className="absolute right-4 z-20 mt-1 w-36 rounded-lg border border-border bg-card shadow-lg text-left"
                      style={{ top: "100%" }}>
                      {["View", "Edit", "Mark as Paid"].map(action => (
                        <button key={action} onClick={() => setActionRow(null)}
                          className="block w-full px-3 py-2 text-xs text-left hover:bg-muted transition-colors first:rounded-t-lg last:rounded-b-lg">
                          {action}
                        </button>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-5 py-3 border-t border-border flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            Showing {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
          </span>
          <div className="flex items-center gap-1">
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
              className="px-2.5 py-1 text-xs rounded border border-border hover:bg-muted disabled:opacity-40 transition-colors">
              ←
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
              <button key={n} onClick={() => setPage(n)}
                className={`w-7 h-7 text-xs rounded border transition-colors ${page === n ? "border-[var(--brand-ring)] text-[var(--brand-primary)] font-semibold" : "border-border hover:bg-muted"}`}>
                {n}
              </button>
            ))}
            <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)}
              className="px-2.5 py-1 text-xs rounded border border-border hover:bg-muted disabled:opacity-40 transition-colors">
              →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Payment Status Breakdown Chart ────────────────────────────────────────────

function PaymentStatusChart({ leads }: { leads: PaymentLead[] }) {
  const totalValue  = leads.reduce((s, l) => s + l.dealValue, 0);
  const [hover, setHover] = useState<PaymentStatus | null>(null);

  const rows = useMemo(() => {
    return ALL_STATUSES.map(s => {
      const group = leads.filter(l => l.status === s);
      const cash  = group.reduce((sum, l) => sum + l.dealValue, 0);
      return { status: s, count: group.length, cash, pct: totalValue > 0 ? cash / totalValue : 0 };
    }).filter(r => r.count > 0);
  }, [leads]);

  // Build single stacked bar segments
  const stackedSegments = useMemo(() =>
    rows.map(r => ({ status: r.status, pct: r.pct, color: STATUS_BAR_COLOR[r.status] })),
  [rows]);

  return (
    <div className="rounded-xl border bg-card p-5 h-full flex flex-col">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold">Payment Status Breakdown</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Total tracked: <span className="font-semibold text-foreground">{fmt(totalValue)}</span>
            <span className="ml-1.5 text-muted-foreground">({leads.length} leads)</span>
          </p>
        </div>
      </div>

      {/* Stacked bar */}
      <div className="h-5 rounded-full overflow-hidden flex mb-5 gap-px bg-muted/30">
        {stackedSegments.map(seg => (
          <div key={seg.status}
            className="h-full transition-all duration-300"
            style={{ width: `${(seg.pct * 100).toFixed(1)}%`, background: seg.color,
              opacity: hover === null || hover === seg.status ? 1 : 0.25 }}
            onMouseEnter={() => setHover(seg.status)}
            onMouseLeave={() => setHover(null)}
          />
        ))}
      </div>

      {/* Rows */}
      <div className="space-y-2.5 flex-1">
        {rows.map(row => (
          <div key={row.status}
            className="group cursor-default"
            onMouseEnter={() => setHover(row.status)}
            onMouseLeave={() => setHover(null)}>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: STATUS_BAR_COLOR[row.status] }} />
                <StatusBadge status={row.status} />
                <span className="text-xs text-muted-foreground">{row.count} lead{row.count !== 1 ? "s" : ""}</span>
              </div>
              <span className="text-xs font-semibold tabular-nums">{fmt(row.cash)}</span>
            </div>
            <div className="h-1.5 rounded-full bg-muted/30 overflow-hidden">
              <div className="h-full rounded-full transition-all duration-300"
                style={{ width: `${(row.pct * 100).toFixed(1)}%`, background: STATUS_BAR_COLOR[row.status],
                  opacity: hover === null || hover === row.status ? 1 : 0.3 }} />
            </div>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-4 pt-3 border-t border-border/50">
        {rows.map(r => (
          <div key={r.status} className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
            <div className="w-2 h-2 rounded-full" style={{ background: STATUS_BAR_COLOR[r.status] }} />
            {(r.pct * 100).toFixed(0)}%
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Pending Payments Aging Table ──────────────────────────────────────────────

const AGING_BUCKETS = [
  { key: "0–3d",  label: "0–3 days",  min: 0,  max: 3  },
  { key: "4–7d",  label: "4–7 days",  min: 4,  max: 7  },
  { key: "8–14d", label: "8–14 days", min: 8,  max: 14 },
  { key: "15+d",  label: "15+ days",  min: 15, max: Infinity },
];

function AgingTable({ leads, reps }: { leads: PaymentLead[]; reps: string[] }) {
  const [bucketF, setBucketF] = useState<string>("all");
  const [repF,    setRepF]    = useState("");

  const pendingLeads = useMemo(() =>
    leads
      .filter(l => l.amountPending > 0)
      .map(l => ({ ...l, days: daysSince(l.closeDate) }))
      .sort((a, b) => b.amountPending - a.amountPending),
  [leads]);

  const filtered = useMemo(() => {
    return pendingLeads.filter(l => {
      if (repF && l.salesRep !== repF) return false;
      if (bucketF !== "all") {
        const b = AGING_BUCKETS.find(b => b.key === bucketF);
        if (b && (l.days < b.min || l.days > b.max)) return false;
      }
      return true;
    });
  }, [pendingLeads, bucketF, repF]);

  // Bucket counts
  const bucketStats = useMemo(() =>
    AGING_BUCKETS.map(b => ({
      ...b,
      count: pendingLeads.filter(l => l.days >= b.min && l.days <= b.max).length,
      cash:  pendingLeads.filter(l => l.days >= b.min && l.days <= b.max)
                         .reduce((s, l) => s + l.amountPending, 0),
    })),
  [pendingLeads]);

  const selectCls = "h-7 text-[11px] rounded-md border border-border bg-background px-2 text-foreground focus:outline-none focus:ring-1 focus:ring-[var(--brand-ring)]";

  return (
    <div className="rounded-xl border bg-card overflow-hidden flex flex-col">
      {/* Header */}
      <div className="px-5 pt-5 pb-0">
        <h3 className="text-sm font-semibold mb-3">Pending Payments Aging</h3>

        {/* Aging bucket chips */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button onClick={() => setBucketF("all")}
            className={`px-3 py-1 rounded-full text-[11px] font-semibold border transition-all ${bucketF === "all" ? "border-[var(--brand-ring)] text-[var(--brand-primary)] bg-[var(--brand-primary)]/5" : "border-border text-muted-foreground hover:text-foreground"}`}>
            All ({pendingLeads.length})
          </button>
          {bucketStats.filter(b => b.count > 0).map(b => (
            <button key={b.key} onClick={() => setBucketF(b.key === bucketF ? "all" : b.key)}
              className={`px-3 py-1 rounded-full text-[11px] font-semibold border transition-all ${bucketF === b.key ? "border-[var(--brand-ring)] text-[var(--brand-primary)] bg-[var(--brand-primary)]/5" : "border-border text-muted-foreground hover:text-foreground"}`}>
              <span>{b.label}</span>
              <span className="ml-1.5 opacity-70">{b.count} · {fmt(b.cash)}</span>
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 mb-3">
          <select value={repF} onChange={e => setRepF(e.target.value)} className={selectCls}>
            <option value="">All Reps</option>
            {reps.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <span className="ml-auto text-[11px] text-muted-foreground">{filtered.length} pending</span>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-auto flex-1">
        <table className="w-full text-xs min-w-[600px]">
          <thead className="sticky top-0 z-10 bg-card border-b border-border">
            <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-2.5 text-left font-semibold">Lead</th>
              <th className="px-3 py-2.5 text-right font-semibold">Pending</th>
              <th className="px-3 py-2.5 text-left font-semibold">Status</th>
              <th className="px-3 py-2.5 text-right font-semibold">Days</th>
              <th className="px-3 py-2.5 text-left font-semibold">Due Date</th>
              <th className="px-3 py-2.5 text-left font-semibold">Rep</th>
              <th className="px-3 py-2.5 text-left font-semibold">Last Contact</th>
              <th className="px-3 py-2.5 text-left font-semibold">Notes</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={8} className="py-10 text-center text-xs text-muted-foreground">No pending payments match this filter.</td></tr>
            ) : filtered.map((row, i) => {
              const isOverdue = row.status === "Overdue";
              return (
                <tr key={row.id}
                  className={`border-b border-border/40 transition-colors hover:bg-muted/20 ${i % 2 !== 0 ? "bg-muted/5" : ""}`}
                  style={isOverdue ? { background: rgba(branding.colors.primary, 0.04) } : undefined}>
                  <td className="px-4 py-2.5 font-semibold text-foreground whitespace-nowrap">{toTitleCase(row.name)}</td>
                  <td className="px-3 py-2.5 text-right font-bold tabular-nums"
                    style={{ color: isOverdue ? branding.colors.primary : branding.colors.chart[0] }}>
                    {fmt(row.amountPending)}
                  </td>
                  <td className="px-3 py-2.5"><StatusBadge status={row.status} /></td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-medium"
                    style={{ color: row.days > 14 ? branding.colors.primary : "var(--foreground)" }}>
                    {row.days}
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap"
                    style={row.dueDate && new Date(row.dueDate) < new Date()
                      ? { color: branding.colors.primary, fontWeight: 600 } : { color: "var(--muted-foreground)" }}>
                    {fmtDate(row.dueDate)}
                  </td>
                  <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{row.salesRep.split(" ")[0]}</td>
                  <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{fmtDate(row.lastContacted)}</td>
                  <td className="px-3 py-2.5 text-muted-foreground max-w-[140px] truncate">{row.notes || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Lead Payment Tracker Section ──────────────────────────────────────────────

interface TrackerProps { startDate: string; endDate: string; source: string; }

function LeadPaymentTracker({ startDate, endDate, source }: TrackerProps) {
  const [leads,    setLeads]    = useState<PaymentLead[]>([]);
  const [programs, setPrograms] = useState<string[]>([]);
  const [reps,     setReps]     = useState<string[]>([]);
  const [loading,  setLoading]  = useState(true);

  const fetchLeads = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ startDate, endDate });
    if (source) params.set("source", source);
    fetch(`/api/mrr/leads?${params}`)
      .then(r => r.json() as Promise<LeadsApiResponse>)
      .then(d => {
        setLeads(d.leads ?? []);
        setPrograms(d.programs ?? []);
        setReps(d.reps ?? []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [startDate, endDate, source]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  return (
    <div className="space-y-5">
      {/* Section header */}
      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-border" />
        <h2 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground px-2">Lead Payment Tracker</h2>
        <div className="h-px flex-1 bg-border" />
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">Loading…</div>
      ) : (
        <>
          <TrackerKpiRow leads={leads} />
          <LeadPaymentTable leads={leads} programs={programs} reps={reps} />
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
            <PaymentStatusChart leads={leads} />
            <AgingTable leads={leads} reps={reps} />
          </div>
        </>
      )}
    </div>
  );
}

// ─── Cash-In Gauge (SVG semi-circle) ──────────────────────────────────────────

function CashGauge({ pct }: { pct: number }) {
  const R = 80;
  const cx = 110;
  const cy = 110;
  const circumference = Math.PI * R;
  const safeP = Math.min(100, Math.max(0, pct));
  const fillLen = (safeP / 100) * circumference;
  const gapLen = circumference - fillLen;
  const startX = cx - R;
  const startY = cy;
  const endX = cx + R;
  const endY = cy;
  const trackD = `M ${startX} ${startY} A ${R} ${R} 0 0 1 ${endX} ${endY}`;
  const dashArray = `${fillLen} ${gapLen}`;
  const color = branding.colors.chart[0];

  return (
    <svg viewBox="0 0 220 120" className="w-full max-w-[280px]">
      <path d={trackD} fill="none" stroke="currentColor" strokeWidth={16}
        className="text-muted/30" strokeLinecap="round" />
      <path d={trackD} fill="none" stroke={color} strokeWidth={16}
        strokeLinecap="round" strokeDasharray={dashArray} strokeDashoffset={0} />
      <text x={cx} y={cy - 8} textAnchor="middle"
        style={{ fontSize: 28, fontWeight: 700, fill: "currentColor" }}>
        {safeP.toFixed(1)}%
      </text>
      <text x={cx} y={cy + 14} textAnchor="middle"
        style={{ fontSize: 11, fill: "var(--muted-foreground)" }}>
        Collection Rate
      </text>
      <text x={startX + 4} y={cy + 18} style={{ fontSize: 10, fill: "var(--muted-foreground)" }}>0%</text>
      <text x={endX - 4} y={cy + 18} textAnchor="end" style={{ fontSize: 10, fill: "var(--muted-foreground)" }}>100%</text>
    </svg>
  );
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────

function KpiCard({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="rounded-xl border bg-card p-4 flex flex-col gap-1">
      <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className="text-2xl font-bold tabular-nums"
        style={accent ? { color: branding.colors.chart[0] } : undefined}>
        {value}
      </span>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export function MRR() {
  const { formatForApi } = useDateRange();
  const { sources: globalSources, source: globalSource } = useFilters();

  const [data, setData] = useState<MrrData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(() => {
    setLoading(true);
    const { startDate, endDate } = formatForApi();
    const params = new URLSearchParams({ startDate, endDate });
    const src = globalSources.join(",") || globalSource;
    if (src) params.set("source", src);

    fetch(`/api/mrr/summary?${params}`)
      .then(r => r.json() as Promise<MrrData>)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [formatForApi, globalSources, globalSource]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const kpis = data?.kpis;
  const funnel = data?.funnel ?? [];

  const { startDate, endDate } = formatForApi();
  const src = globalSources.join(",") || globalSource;
  const cohortFilters = { startDate, endDate, ...(src ? { source: src } : {}) };

  return (
    <div className="space-y-8">
      <PageHeader title="MRR" description="Cash-in and lead-to-cash overview." />

      {/* KPI Row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <KpiCard label="Expected Cash"  value={kpis ? fmt(kpis.expectedCash)               : "—"} />
        <KpiCard label="Collected Cash" value={kpis ? fmt(kpis.collectedCash)              : "—"} accent />
        <KpiCard label="Pending Cash"   value={kpis ? fmt(kpis.pendingCash)                : "—"} />
        <KpiCard label="Collection %"   value={kpis ? `${kpis.collectionPct.toFixed(1)}%`  : "—"} accent />
        <KpiCard label="Closed Won"     value={kpis ? String(kpis.closedWonDeals)           : "—"} />
      </div>

      {/* Cash-In Section */}
      <div className="rounded-xl border bg-card p-6">
        <h2 className="text-base font-semibold mb-6">Cash-In</h2>
        {loading ? (
          <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">Loading…</div>
        ) : (
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-shrink-0 flex flex-col items-center gap-3">
              <CashGauge pct={kpis?.collectionPct ?? 0} />
            </div>
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-4 w-full">
              <div className="rounded-lg border bg-background p-4 flex flex-col gap-1">
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Expected</span>
                <span className="text-xl font-bold tabular-nums">{kpis ? fmt(kpis.expectedCash) : "—"}</span>
              </div>
              <div className="rounded-lg border bg-background p-4 flex flex-col gap-1">
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Collected</span>
                <span className="text-xl font-bold tabular-nums" style={{ color: branding.colors.chart[0] }}>
                  {kpis ? fmt(kpis.collectedCash) : "—"}
                </span>
              </div>
              <div className="rounded-lg border bg-background p-4 flex flex-col gap-1">
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Pending</span>
                <span className="text-xl font-bold tabular-nums">{kpis ? fmt(kpis.pendingCash) : "—"}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Cash Collection Cohort */}
      <CashCollectionCohortChart filters={cohortFilters} />

      {/* Lead-to-Cash Breakdown */}
      <div className="rounded-xl border bg-card p-6">
        <h2 className="text-base font-semibold mb-4">Lead-to-Cash Breakdown</h2>
        {loading ? (
          <div className="flex items-center justify-center h-24 text-sm text-muted-foreground">Loading…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="pb-2 text-left font-medium">Stage</th>
                  <th className="pb-2 text-right font-medium">Count</th>
                  <th className="pb-2 text-right font-medium">Expected Cash</th>
                  <th className="pb-2 text-right font-medium">Collected Cash</th>
                  <th className="pb-2 text-right font-medium">Pending Cash</th>
                  <th className="pb-2 text-right font-medium">Conv %</th>
                </tr>
              </thead>
              <tbody>
                {funnel.map((row, i) => (
                  <tr key={row.stage}
                    className={`border-b border-border/50 ${i % 2 === 0 ? "" : "bg-muted/20"}`}>
                    <td className="py-3 font-medium">{row.stage}</td>
                    <td className="py-3 text-right tabular-nums">{row.count.toLocaleString()}</td>
                    <td className="py-3 text-right tabular-nums text-muted-foreground">
                      {row.expected != null ? fmt(row.expected) : "—"}
                    </td>
                    <td className="py-3 text-right tabular-nums font-medium"
                      style={row.collected != null && row.collected > 0 ? { color: branding.colors.chart[0] } : undefined}>
                      {row.collected != null ? fmt(row.collected) : "—"}
                    </td>
                    <td className="py-3 text-right tabular-nums text-muted-foreground">
                      {row.pending != null ? fmt(row.pending) : "—"}
                    </td>
                    <td className="py-3 text-right tabular-nums">
                      {row.convPct != null ? (
                        <span className="inline-flex items-center justify-end gap-1">
                          <span className="inline-block h-1.5 rounded-full"
                            style={{ width: `${Math.min(row.convPct, 100) * 0.4}px`, backgroundColor: branding.colors.chart[0], minWidth: 4 }} />
                          {row.convPct.toFixed(1)}%
                        </span>
                      ) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Lead Payment Tracker */}
      <LeadPaymentTracker startDate={startDate} endDate={endDate} source={src} />
    </div>
  );
}
